import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as ExposureCreators } from "modules/analyze-historical-exposure/model"
import { getDisplayType, getDisplayTypeName } from "modules/analyze-historical-exposure/selectors/items"
import { getBasePositionSet, getBaseSignature } from "selectors/base"
import { getUnmodeledDates } from "selectors/dates"
import { getEarliestDate, getLatestDate } from "modules/portfolio-root/selectors/dates"
import { getSelectedModelId, getViewType } from "modules/portfolio-root/selectors/portfolios"
import { Creators as notificationsCreators } from "modules/notifications/model"
import createModule from "../createModule"
import view from "./view"
import { getRows } from "./selectors/rows"
import { getFileName } from "./selectors/file"
import { getSelectedDate } from "./selectors/dates"
import { getFactors, getGroups } from "./selectors/factors"
import { Creators } from "./model"
import { generateClassificationQueryForContributors, getClassifications } from "helpers/classification"
import { getStyleAggregation } from "selectors/styleAggregation"
import { Creators as PortfolioRootCreators } from "modules/portfolio-root/model"
import { VIEW_TYPES } from "helpers/base"
import { MODEL_METADATA_QUERY } from "resources/models"

const getExposureQuery = (classifications) => `
query ($modelId: String!, $displayType: [String], $on: Date!, $positionSet: PositionSetInput!, $contentSetId: String) {
  model (id: $modelId) {
    simulation(positionSet: $positionSet, from: $on, to: $on) {
      composition {
        referenceEquity
        positions {
          assetClass
          id
          economicExposure
          bloombergTicker
        }
      }
      exposure (contentSetId: $contentSetId) {
        factors (category: $displayType) {
          id
          category
          net
          long
          short
        }
      }
      exposureContributors (contentSetId: $contentSetId) {
        contributors {
          id
          assetClass
          country
          description
          currency
          ${generateClassificationQueryForContributors("GICS", classifications)}
          factors (category: $displayType) {
            id
            category
            securityExposure
          }
        }
      }
    }
  },
}`

export default createModule({
  view,
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    portfolioId: state.global.selected.portfolio,
    modelId: getSelectedModelId(state, props),
    base: getBasePositionSet(state, props),
    baseSignature: getBaseSignature(state, props),
    selectedDate: getSelectedDate(state, props),
    displayType: getDisplayType(state, props),
    viewType: getViewType(state, props),
    classifications: getClassifications(state),
    isStyleFactorsAdditive: false,
  }),
  required: ["portfolioId", "account", "modelId"],
  data: [{
    resource: "portfolioExposure",
    as: null,
    url: ({ modelId, portfolioId, selectedDate, displayType, contentSetId, classifications }) => ({
      id: `${modelId}${portfolioId}${selectedDate}${displayType}${contentSetId}`,
      query: getExposureQuery(classifications),
      variables: {
        modelId,
        on: selectedDate,
        displayType,
        contentSetId,
        positionSet: {
          type: "PORTFOLIO",
          id: portfolioId,
        },
      },
    }),
  }, {
    resource: "portfolioActiveExposure",
    as: null,
    url: ({ viewType, modelId, portfolioId, selectedDate, displayType, base, baseSignature, contentSetId }) => {
      if (viewType === VIEW_TYPES.ABSOLUTE) {
        return null
      }

      return {
        id: `${modelId}${portfolioId}${selectedDate}${baseSignature}${displayType}${contentSetId}`,
        query: `query ($modelId: String!, $displayType: [String], $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!, $positionSetBase: PositionSetInput!, $contentSetId: String) {
          model (id: $modelId) {
            simulation (positionSet: $positionSetBase, from: $on, to: $on) {
              exposure (contentSetId: $contentSetId) {
                factors (category: $displayType) {
                  id
                  net
                  category
                }
              }
            }
            active: simulation (positionSet: $positionSet, from: $on, to: $on, base: $base) {
              exposure (contentSetId: $contentSetId) {
                factors (category: $displayType) {
                  id
                  net
                  category
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          displayType,
          positionSet: {
            type: "PORTFOLIO",
            id: portfolioId,
          },
          contentSetId,
          positionSetBase: base,
          base: [base],
          on: selectedDate,
        },
      }
    },
  },
  MODEL_METADATA_QUERY,
  ],
  mapStateToProps: (state, props) => ({
    rows: getRows(state, props),
    factors: getFactors(state, props),
    groups: getGroups(state, props),
    earliestDate: getEarliestDate(state, props),
    latestDate: getLatestDate(state, props),
    fileName: getFileName(state, props),
    unmodeledDates: getUnmodeledDates(state, props),
    displayTypeName: getDisplayTypeName(state, props),
    ...getStyleAggregation(state, props),
  }),
  actions: {
    ...Creators,
    openSecurityProfile: ProfileCreators.openSecurityProfile,
    openFactorProfile: ProfileCreators.openFactorProfile,
    toggleLitmusView: ExposureCreators.toggleLitmusView,
    addNotification: notificationsCreators.add,
    setAggregationFn: PortfolioRootCreators.setAggregationFn,
  },
})
